import React from 'react';
import { ArrowLeft, Calendar as CalendarIcon, MapPin, Clock, Users, MessageSquare, CheckCircle2 } from 'lucide-react';
import { events, eventAttendees, people } from '../../data/mockData';
interface EventDetailProps {
  eventId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function EventDetail({
  eventId,
  onNavigate
}: EventDetailProps) {
  const event = events.find(e => e.id === eventId);
  if (!event) return <div data-id="element-275">Event nicht gefunden</div>;
  const isLive = event.status === 'live';
  // Get attendees with their full person profiles
  const attendeesWithProfiles = eventAttendees.filter(ea => ea.eventId === event.id).map(ea => ({
    ...ea,
    person: people.find(p => p.id === ea.personId)!
  })).filter(ea => ea.person); // Ensure person exists
  // Mock "relevant" contacts (e.g. same industry/interests as current user)
  const relevantAttendees = attendeesWithProfiles.slice(0, 4);
  const otherAttendees = attendeesWithProfiles.slice(4);
  return <div className="space-y-6" data-id="element-276">
      <button onClick={() => onNavigate('events')} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-277">
        <ArrowLeft size={16} data-id="element-278" />
        Zurück zu Events
      </button>

      {/* Event Hero */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative" data-id="element-279">
        {isLive && <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500" data-id="element-280"></div>}
        <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8" data-id="element-281">
          <div className="w-32 h-32 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-6xl shadow-inner flex-shrink-0" data-id="element-282">
            {event.imageEmoji}
          </div>

          <div className="flex-1" data-id="element-283">
            <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-284">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${event.type === 'homeGame' ? 'bg-red-100 text-red-700' : event.type === 'business' ? 'bg-blue-100 text-blue-700' : event.type === 'vip' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-285">
                {event.type === 'homeGame' ? 'Heimspiel' : event.type}
              </span>
              {isLive && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700" data-id="element-286">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-fast" data-id="element-287"></span>
                  Live
                </span>}
            </div>

            <h1 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-288">
              {event.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 mb-6" data-id="element-289">
              <div className="flex items-center gap-2" data-id="element-290">
                <CalendarIcon size={18} className="text-slate-400" data-id="element-291" />
                {new Date(event.date).toLocaleDateString('de-DE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              </div>
              <div className="flex items-center gap-2" data-id="element-292">
                <Clock size={18} className="text-slate-400" data-id="element-293" />
                {event.time} Uhr
              </div>
              <div className="flex items-center gap-2" data-id="element-294">
                <MapPin size={18} className="text-slate-400" data-id="element-295" />
                {event.location}
              </div>
            </div>

            <p className="text-slate-600 leading-relaxed max-w-3xl" data-id="element-296">
              {event.description}
            </p>
          </div>

          {/* QR Code / Access Area */}
          <div className="w-full md:w-64 bg-slate-50 rounded-xl p-5 border border-slate-100 flex flex-col items-center justify-center text-center" data-id="element-297">
            <div className="w-32 h-32 bg-white p-2 rounded-lg shadow-sm border border-slate-200 mb-4 flex items-center justify-center" data-id="element-298">
              {/* Mock QR Code */}
              <div className="grid grid-cols-5 grid-rows-5 gap-1 w-full h-full opacity-80" data-id="element-299">
                {Array.from({
                length: 25
              }).map((_, i) => <div key={i} className={`bg-slate-800 ${Math.random() > 0.5 ? 'rounded-sm' : ''}`} style={{
                opacity: Math.random() > 0.3 ? 1 : 0
              }} data-id="element-300"></div>)}
              </div>
            </div>
            <h3 className="font-bold text-slate-900 mb-1" data-id="element-301">Ihr Event-Zugang</h3>
            <p className="text-xs text-slate-500 mb-4" data-id="element-302">
              Zeigen Sie diesen Code am Eingang vor.
            </p>
            <button className="w-full py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-303">
              In Apple Wallet
            </button>
          </div>
        </div>
      </div>

      {/* HERO FEATURE: Wer ist heute da? */}
      <div className="space-y-6" data-id="element-304">
        <div className="flex items-center justify-between" data-id="element-305">
          <div data-id="element-306">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-307">
              Wer ist heute da? <span className="text-2xl" data-id="element-308">👀</span>
            </h2>
            <p className="text-slate-500 mt-1 flex items-center gap-2" data-id="element-309">
              <Users size={16} data-id="element-310" />
              {attendeesWithProfiles.filter(a => a.checkedIn).length} von{' '}
              {event.attendeeCount} angemeldeten Personen sind bereits vor Ort
            </p>
          </div>
          <div className="flex gap-2" data-id="element-311">
            <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-312">
              Nach Branche filtern
            </button>
          </div>
        </div>

        {/* Relevante Kontakte (Highlighted) */}
        {relevantAttendees.length > 0 && <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-6" data-id="element-313">
            <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-314">
              <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-315"></span>
              Besonders relevant für Sie
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-316">
              {relevantAttendees.map(({
            person,
            checkedIn,
            checkInTime
          }) => <div key={person.id} onClick={() => onNavigate('person-profile', person.id)} className="bg-white rounded-xl p-4 border border-emerald-200 shadow-sm hover:shadow-md transition-all cursor-pointer relative overflow-hidden group" data-id="element-317">
                  <div className="absolute top-0 left-0 w-1 h-full bg-emerald-400" data-id="element-318"></div>
                  <div className="flex items-start gap-4" data-id="element-319">
                    <div className="relative" data-id="element-320">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-600" data-id="element-321">
                        {person.avatar}
                      </div>
                      {checkedIn && <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5" data-id="element-322">
                          <CheckCircle2 size={16} className="text-emerald-500" data-id="element-323" />
                        </div>}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-324">
                      <h4 className="font-bold text-slate-900 truncate group-hover:text-emerald-600 transition-colors" data-id="element-325">
                        {person.firstName} {person.lastName}
                      </h4>
                      <p className="text-xs text-slate-500 truncate" data-id="element-326">
                        {person.role}
                      </p>
                      <p className="text-xs font-medium text-slate-700 truncate mt-0.5" data-id="element-327">
                        {person.companyName}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2" data-id="element-328">
                    <button onClick={e => {
                e.stopPropagation();
                onNavigate('messages');
              }} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors" data-id="element-329">
                      <MessageSquare size={14} data-id="element-330" /> Treffen
                    </button>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Alle anderen Teilnehmer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-331">
          {otherAttendees.map(({
          person,
          checkedIn,
          checkInTime
        }) => <div key={person.id} onClick={() => onNavigate('person-profile', person.id)} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group" data-id="element-332">
              <div className="flex items-start gap-3" data-id="element-333">
                <div className="relative" data-id="element-334">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600" data-id="element-335">
                    {person.avatar}
                  </div>
                  {checkedIn && <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5" data-id="element-336">
                      <CheckCircle2 size={14} className="text-emerald-500" data-id="element-337" />
                    </div>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-338">
                  <h4 className="font-bold text-slate-900 text-sm truncate group-hover:text-emerald-600 transition-colors" data-id="element-339">
                    {person.firstName} {person.lastName}
                  </h4>
                  <p className="text-xs text-slate-500 truncate" data-id="element-340">
                    {person.role}
                  </p>
                  <p className="text-xs text-slate-600 truncate mt-0.5" data-id="element-341">
                    {person.companyName}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}